import { useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormGroup } from "@mui/material";
import { DropDown } from "../../../../../../common/domain/models/dropdown";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { TableOptions } from "../../../../../../common/components/table_options";
import { ContasReceberAnaliticoFilter } from "../../../../domain/models/contas_receber_analitico_filter";
import { defaultReportContasReceberAnaliticoForm, ReportContasReceberAnaliticoColumnsForm, ReportContasReceberAnaliticoForm, ReportContasReceberAnaliticoSchema } from "../../../schemas/reports/contasReceberAnaliticoReport";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";

/**
 * ReportMapaConcretagemModal
 */

type Props = {
  open: boolean;
  loading: boolean;
  saving: boolean;
  ddlCentroCusto: CentroCustoDropDown[];
  ddlBanco: DropDown[];
  ddlSubconta: DropDown[];
  ddlCliente: DropDown[];

  filtros: ContasReceberAnaliticoFilter | null,
  onClose: (openState: boolean) => void;
  onSubmit: (form: ReportContasReceberAnaliticoForm) => void;
  onSubmitColumns: (colunas: ReportContasReceberAnaliticoColumnsForm) => void;

};

export const ReportReceberAnaliticoModal = ({
  open,
  loading,
  saving,
  ddlCentroCusto,
  ddlBanco,
  ddlSubconta,
  ddlCliente,
  filtros,
  onClose,
  onSubmit,
  onSubmitColumns,
}: Props) => {

  const hookForm = useForm<ReportContasReceberAnaliticoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: defaultReportContasReceberAnaliticoForm,
    resolver: zodResolver(ReportContasReceberAnaliticoSchema),
  });

  const [c] = hookForm.watch(['colunas']);

  const status = useMemo(() => {
    return [
      { id: 0, label: 'Em Aberto' },
      { id: 1, label: 'Recebidas' },
      { id: 2, label: 'Outros' },
      { id: 3, label: 'Atrasadas' },
    ];
  }, []);

  const ordenacao = useMemo(() => {
    return [
      { id: 0, label: 'Data Receber Crescente' },
      { id: 1, label: 'Data Receber Decrescente' },
      { id: 2, label: 'Data Recebido Crescente' },
      { id: 3, label: 'Data Recebido Decrescente' },
    ];
  }, []);

  const selectAll = useMemo(() => {
    if (!c) return false;
    return (
      c.nroContasPagar &&
      c.centroCusto &&
      c.bancoPagamento &&
      c.notaFiscal &&
      c.nroDocumento &&
      c.status &&
      c.titulo &&
      c.dataPagar &&
      c.dataPago &&
      c.valorPagar &&
      c.valorPago &&
      c.observacao &&
      c.subconta &&
      c.cliente &&
      c.nroContratoFinanceiro &&
      c.multa &&
      c.juros &&
      c.desconto &&
      c.bdi &&
      c.bdi2 &&
      c.bdiTotal &&
      c.bdiTotal2 &&
      c.dataCriacao &&
      c.dataCompetencia
    );
  }, [c]);



  useEffect(() => {
    if (open) {
      hookForm.reset({
        ...defaultReportContasReceberAnaliticoForm,
      });

      if (filtros && filtros.colunas && filtros.colunas.length > 0) {
        const colunasArr = filtros.colunas.split(',');

        hookForm.setValue('colunas', {
          nroContasPagar: colunasArr.includes('NR_CONTAS_PAGAR'),
          centroCusto: colunasArr.includes('CENTRO_CUSTO'),
          bancoPagamento: colunasArr.includes('BANCO_PAGAMENTO'),
          notaFiscal: colunasArr.includes('NOTA_FISCAL'),
          nroDocumento: colunasArr.includes('NRO_DOCUMENTO'),
          status: colunasArr.includes('STATUS'),
          titulo: colunasArr.includes('TITULO'),
          dataPagar: colunasArr.includes('DATA_A_PAGAR'),
          dataPago: colunasArr.includes('DATA_PAGO'),
          valorPagar: colunasArr.includes('VALOR_A_PAGAR'),
          valorPago: colunasArr.includes('VALOR_PAGO'),
          observacao: colunasArr.includes('OBSERVACOES'),
          subconta: colunasArr.includes('SUBCONTA'),
          cliente: colunasArr.includes('CLIENTE'),
          nroContratoFinanceiro: colunasArr.includes('NRO_CONTRATO_FINANCEIRO'),
          multa: colunasArr.includes('MULTA'),
          juros: colunasArr.includes('JUROS'),
          desconto: colunasArr.includes('DESCONTO'),
          bdi: colunasArr.includes('BDI'),
          bdi2: colunasArr.includes('BDI2'),
          bdiTotal: colunasArr.includes('BDI_TOTAL'),
          bdiTotal2: colunasArr.includes('BDI_TOTAL2'),
          dataCriacao: colunasArr.includes('DATA_CRIACAO'),
          dataCompetencia: colunasArr.includes('DATA_COMPETENCIA'),
        })
      }
    }
  }, [open, filtros, hookForm]);

  const handleToggleSelectAll = useCallback((checked: boolean) => {
    hookForm.setValue('colunas', {
      nroContasPagar: checked,
      centroCusto: checked,
      bancoPagamento: checked,
      notaFiscal: checked,
      nroDocumento: checked,
      status: checked,
      titulo: checked,
      dataPagar: checked,
      dataPago: checked,
      valorPagar: checked,
      valorPago: checked,
      observacao: checked,
      subconta: checked,
      cliente: checked,
      nroContratoFinanceiro: checked,
      multa: checked,
      juros: checked,
      desconto: checked,
      bdi: checked,
      bdi2: checked,
      bdiTotal: checked,
      bdiTotal2: checked,
      dataCriacao: checked,
      dataCompetencia: checked,
    });
  }, [hookForm]);

  const handleToggleOptionChange = useCallback((field: keyof ReportContasReceberAnaliticoForm['colunas'], checked: boolean) => {
    const colunas = hookForm.getValues('colunas');
    colunas[field] = checked;
    hookForm.setValue('colunas', { ...colunas });
  }, [
    hookForm,
  ]);

  const handleLimparCampos = useCallback(() => {
    const colunas = hookForm.getValues('colunas');
    hookForm.reset({
      ...defaultReportContasReceberAnaliticoForm,
      colunas,
    });
  }, [hookForm]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title={'Relatório Contas Receber Analítico'}
      width='80rem'
      confirmButtonLabel='Gerar relatório'
      confirmSavingButtonLabel='Gerando relatório...'
      savingLabel="Gerando"
      leftButtonSlot={
        <div className="w-full flex flex-row justify-between">
          <div>
            <PrimaryButton onClick={() => onSubmitColumns(c)}>Salvar Colunas</PrimaryButton>
          </div>
          <div className="w-[120px]">
            <PrimaryButton
              type="button"
              buttonColor="yellow"
              onClick={() => handleLimparCampos()}>
              Limpar campos
            </PrimaryButton>
          </div>
        </div>
      }
      onClose={() => onClose(false)}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className="w-full grid grid-rows-1 gap-2 p-4">
        <div className="col-start-1 px-8 py-8">
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <div className="w-full flex gap-1">
                <DefaultFormInput
                  name='titulo'
                  label='Título'
                  reactHookForm={hookForm} />
              </div>

              <DefaultFormMultiAutocomplete
                name='centroCusto'
                label='Centro de Custo'
                options={ddlCentroCusto}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='tipoConta'
                label='Banco de Pagamento'
                options={ddlBanco}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='subconta'
                label='Subconta'
                options={ddlSubconta}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='cliente'
                label='Cliente'
                options={ddlCliente}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <FormMaskedInput
                name={'nroContratoFinanceiro'}
                label={"Quantidade de unidades?"}
                mask='00000'
                maskPlaceholder="______"
                reactHookForm={hookForm}
              />

              <DefaultFormMultiAutocomplete
                name='status'
                label='Status'
                options={status}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.label}
                reactHookForm={hookForm} />


              <div>
                Data Competência:
                <div className="w-full flex gap-1">
                  <FormDatePickerInput
                    name='dataCompetenciaDe'
                    label='Data De'
                    reactHookForm={hookForm} />

                  <FormDatePickerInput
                    name='dataCompetenciaAte'
                    label='Data Ate'
                    reactHookForm={hookForm} />
                </div>
              </div>


              <div>
                Data Receber:
                <div className="w-full flex gap-1">

                  <FormDatePickerInput
                    name='dataReceberDe'
                    label='Data De'
                    reactHookForm={hookForm} />

                  <FormDatePickerInput
                    name='dataReceberAte'
                    label='Data Ate'
                    reactHookForm={hookForm} />

                </div>
              </div>

              <div>
                Data Recebido:
                <div className="w-full flex gap-1">

                  <FormDatePickerInput
                    name='dataRecebidoDe'
                    label='Data De'
                    reactHookForm={hookForm} />

                  <FormDatePickerInput
                    name='dataRecebidoAte'
                    label='Data Ate'
                    reactHookForm={hookForm} />

                </div>
              </div>

              <div>
                Data Criação:
                <div className="w-full flex gap-1">
                  <FormDatePickerInput
                    name='dataCriacaoDe'
                    label='Data De'
                    reactHookForm={hookForm} />

                  <FormDatePickerInput
                    name='dataCriacaoAte'
                    label='Data Ate'
                    reactHookForm={hookForm} />

                </div>
              </div>


            </>
          </FormContainer>
        </div>

        <div className="col-start-6 col-span-5 px-8 py-8">
          <FormGroup>
            <TableOptions label="EXIBIR TODAS AS COLUNAS" checked={selectAll} onChange={c => handleToggleSelectAll(c)} />
            <div className="flex flex-col pl-4">
              <TableOptions checked={c?.nroContasPagar} label="Nº CONTA" onChange={v => handleToggleOptionChange('nroContasPagar', v)} />
              <TableOptions checked={c?.centroCusto} label="CENTRO DE CUSTO" onChange={v => handleToggleOptionChange('centroCusto', v)} />
              <TableOptions checked={c?.bancoPagamento} label="BANCO DE PAGAMENTO" onChange={v => handleToggleOptionChange('bancoPagamento', v)} />
              <TableOptions checked={c?.notaFiscal} label="NOTA FISCAL" onChange={v => handleToggleOptionChange('notaFiscal', v)} />
              <TableOptions checked={c?.nroDocumento} label="Nº DOCUMENTO" onChange={v => handleToggleOptionChange('nroDocumento', v)} />
              <TableOptions checked={c?.status} label="STATUS" onChange={v => handleToggleOptionChange('status', v)} />
              <TableOptions checked={c?.titulo} label="TÍTULO" onChange={v => handleToggleOptionChange('titulo', v)} />
              <TableOptions checked={c?.dataPagar} label="DATA A RECEBER" onChange={v => handleToggleOptionChange('dataPagar', v)} />
              <TableOptions checked={c?.dataPago} label="DATA RECEBIDO" onChange={v => handleToggleOptionChange('dataPago', v)} />
              <TableOptions checked={c?.valorPagar} label="VALOR A RECEBER" onChange={v => handleToggleOptionChange('valorPagar', v)} />
              <TableOptions checked={c?.valorPago} label="VALOR RECEBIDO" onChange={v => handleToggleOptionChange('valorPago', v)} />
              <TableOptions checked={c?.observacao} label="OBSERVAÇÕES" onChange={v => handleToggleOptionChange('observacao', v)} />
              <TableOptions checked={c?.subconta} label="SUBCONTA" onChange={v => handleToggleOptionChange('subconta', v)} />
              <TableOptions checked={c?.cliente} label="CLIENTE" onChange={v => handleToggleOptionChange('cliente', v)} />
              <TableOptions checked={c?.nroContratoFinanceiro} label="Nº CONTRATO FINANCEIRO" onChange={v => handleToggleOptionChange('nroContratoFinanceiro', v)} />
              <TableOptions checked={c?.multa} label="MULTA" onChange={v => handleToggleOptionChange('multa', v)} />
              <TableOptions checked={c?.juros} label="JUROS" onChange={v => handleToggleOptionChange('juros', v)} />
              <TableOptions checked={c?.desconto} label="DESCONTO" onChange={v => handleToggleOptionChange('desconto', v)} />
              <TableOptions checked={c?.bdi} label="BDI" onChange={v => handleToggleOptionChange('bdi', v)} />
              <TableOptions checked={c?.bdi2} label="BDI2" onChange={v => handleToggleOptionChange('bdi2', v)} />
              <TableOptions checked={c?.bdiTotal} label="BDI TOTAL" onChange={v => handleToggleOptionChange('bdiTotal', v)} />
              <TableOptions checked={c?.bdiTotal2} label="BDI TOTAL2" onChange={v => handleToggleOptionChange('bdiTotal2', v)} />
              <TableOptions checked={c?.dataCriacao} label="DATA CRIAÇÃO" onChange={v => handleToggleOptionChange('dataCriacao', v)} />
              <TableOptions checked={c?.dataCompetencia} label="DATA COMPETÊNCIA" onChange={v => handleToggleOptionChange('dataCompetencia', v)} />
            </div>
          </FormGroup>
          <div className="mt-4">
            <DefaultFormAutocomplete
              name='ordenacao'
              label='Ordenação'
              options={ordenacao}
              getOptionKey={x => x.id}
              getOptionLabel={x => x.label}
              reactHookForm={hookForm}
            />
          </div>
        </div>
      </div>
    </BaseFormControlledModal>
  );
}